import { TrackingContext, type TrackingContextValue, sportsEvent } from '@ps/web-shared-analytics';
import type { MarketTemplate as MarketTemplateType } from '@ps/web-sports-api-types';
import React, { useContext } from 'react';
import { useSelector } from 'react-redux';

import type { RootState } from '@swf/core/store';
import { ExpandablePanel, MarketTemplate, Title } from '@swf/sports/components';
import { useEventMarketTemplatesWidget } from '@swf/sports/models/market-templates/eventBased';
import { isSingleMarketTemplate } from '@swf/sports/utils/isSingleMarketTemplate';
import mergeWithTrackingContextValue from '@swf/sports/utils/mergeWithTrackingContextValue';

import styles from './GroupedMarketTemplate.scss';
import Header from './components/Header/Header';

const GroupedMarketTemplate: React.FC<{
  groupedMarkets: MarketTemplateType[][];
  openInitially: boolean;
}> = ({ groupedMarkets, openInitially }) => {
  const trackingContextValue = useContext(TrackingContext);
  const { widgetSelect } = useEventMarketTemplatesWidget();

  const markets = useSelector(widgetSelect.markets);

  const { marketId = '' } = groupedMarkets[0].find(isSingleMarketTemplate) ?? {};
  const { eventId = 0 } = markets[marketId] ?? {};

  const event = useSelector((rootState: RootState) => widgetSelect.event(rootState, eventId));

  const appendTrackingEntities = (
    trackingContext: TrackingContextValue,
    data: { subCompetitionName?: string; subCompetitionId: number; subCompetitionStartTime: string }
  ): TrackingContextValue =>
    mergeWithTrackingContextValue(trackingContext, [
      sportsEvent({
        eventId: data.subCompetitionId,
        eventStartTime: new Date(data.subCompetitionStartTime).getTime()
      })
    ]);

  return (
    <TrackingContext.Provider
      value={appendTrackingEntities(trackingContextValue, {
        subCompetitionId: event.eventId,
        subCompetitionStartTime: event.eventStartTime
      })}
    >
      <div className={styles.wrapper}>
        <ExpandablePanel openInitially={openInitially} title={<Title>{event.eventName}</Title>}>
          {groupedMarkets.map((marketGroup) => {
            const { marketId = '' } = marketGroup.find(isSingleMarketTemplate) ?? {};
            const firstMarket = markets[marketId];

            if (!firstMarket) return null;

            return (
              <React.Fragment key={`market-group-${firstMarket.marketId}`}>
                <Header date={new Date(firstMarket.marketTime)} />
                {marketGroup.map((props) => (
                  <MarketTemplate
                    key={isSingleMarketTemplate(props) ? props.marketId : props.marketIds.join()}
                    {...props}
                  />
                ))}
              </React.Fragment>
            );
          })}
        </ExpandablePanel>
      </div>
    </TrackingContext.Provider>
  );
};

export default GroupedMarketTemplate;
